'use client'

import type { CSSProperties, ReactNode } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import type { PublicationDraft } from '@/lib/publications/types'

export type BeautyOverlayTone = 'black' | 'white' | 'none'

export interface BeautyPackProps {
  draft: PublicationDraft
  activeIndex?: number
  onActiveIndexChange?: (index: number) => void
  showControls?: boolean
  exportMode?: boolean
}

export interface BeautyVisualDefaults {
  overlay?: BeautyOverlayTone
  opacity?: number
  blur?: boolean
}

export interface BeautyCopyFallback {
  eyebrow?: string
  headline?: string
  subheadline?: string
  body?: string
  cta?: string

  proof?: string
  result?: string
  question?: string
  quote?: string
  author?: string

  offer?: string
  urgency?: string
  stat?: string
  statLabel?: string

  bullets?: string[]
  steps?: any[]
  timeline?: any[]

  beforeAfter?: {
    before?: string
    after?: string
  }

  stats?: any[]
  offerBox?: any
  testimonial?: any
}

export const beautySerif = '"Cormorant Garamond", "Georgia", "Times New Roman", serif'
export const beautySans = '"Avenir Next", "Helvetica Neue", Arial, sans-serif'
export const beautyInk = '#211815'
export const beautyCream = '#F6EEE3'
export const beautyIvory = '#FFF9F0'
export const beautyMuted = 'rgba(33,24,21,0.58)'

export function clampIndex(index: number | undefined, total: number) {
  const safeTotal = Math.max(total, 1)
  const safeIndex = Number.isFinite(index) ? Number(index) : 0
  return Math.min(Math.max(safeIndex, 0), safeTotal - 1)
}

export function getBeautyDesign(draft: PublicationDraft, fallbackAccent = '#B18A4A') {
  const design = ((draft as any).design || {}) as Record<string, any>

  return {
    accent: design.accent || fallbackAccent,
    accentSoft: design.accentSoft || design.accent || fallbackAccent,
    accentDeep: design.accentDeep || design.accent || fallbackAccent,
    text: design.text || beautyInk,
    textMuted: design.textMuted || beautyMuted,
    surface: design.surface || beautyIvory,
    border: design.border || 'rgba(177,138,74,0.26)',
  }
}

export function getBeautyVisual(
  draft: PublicationDraft,
  activeIndex: number,
  fallbackSrc: string,
  defaults?: BeautyVisualDefaults
) {
  const visuals = Array.isArray((draft as any).slideVisuals) ? (draft as any).slideVisuals : []
  const visual = visuals[activeIndex] || {}

  return {
    src: visual.backgroundImage || fallbackSrc,
    blur: visual.blurEnabled ?? defaults?.blur ?? false,
    overlay: (visual.overlayTone || defaults?.overlay || 'black') as BeautyOverlayTone,
    opacity: visual.overlayOpacity ?? defaults?.opacity ?? 0.35,
  }
}

export function getBeautyCopy(
  draft: PublicationDraft,
  activeIndex: number,
  fallback: BeautyCopyFallback = {}
) {
  const slides = Array.isArray((draft as any)?.slides) ? (draft as any).slides : []
  const slide = slides[activeIndex] || {}

  const normalizeStringArray = (value: any): string[] => {
    if (!Array.isArray(value)) return []

    return value
      .map((item) => {
        if (typeof item === 'string') return item.trim()

        if (item && typeof item === 'object') {
          return String(item.title || item.text || item.label || item.headline || '').trim()
        }

        return ''
      })
      .filter(Boolean)
  }

  const normalizeSteps = (value: any): any[] => {
    if (!Array.isArray(value)) return []

    return value
      .map((item, index) => {
        if (typeof item === 'string') {
          return {
            label: `0${index + 1}`,
            title: item.trim(),
            text: '',
          }
        }

        if (item && typeof item === 'object') {
          return {
            label: String(item.label || `0${index + 1}`).trim(),
            title: String(item.title || item.headline || item.text || '').trim(),
            text: String(item.text || item.body || item.description || '').trim(),
          }
        }

        return null
      })
      .filter((item) => item?.title || item?.text)
  }

  const bullets =
    normalizeStringArray(slide.bullets).length > 0
      ? normalizeStringArray(slide.bullets)
      : normalizeStringArray(slide.items).length > 0
        ? normalizeStringArray(slide.items)
        : fallback.bullets || []

  const steps =
    normalizeSteps(slide.steps).length > 0
      ? normalizeSteps(slide.steps)
      : normalizeSteps(slide.items).length > 0
        ? normalizeSteps(slide.items)
        : Array.isArray(fallback.steps) && fallback.steps.length
          ? fallback.steps
          : bullets.map((item, index) => ({
              label: `0${index + 1}`,
              title: item,
              text: '',
            }))

  const timeline = Array.isArray(slide.timeline) && slide.timeline.length
    ? slide.timeline
    : fallback.timeline || []

  const stats = Array.isArray(slide.stats) && slide.stats.length
    ? slide.stats
    : fallback.stats || []

  const testimonial = slide.testimonial || fallback.testimonial || null
  const offerBox = slide.offerBox || fallback.offerBox || null

  return {
    eyebrow: slide.eyebrow || slide.kicker || fallback.eyebrow || '',
    headline: slide.headline || slide.title || fallback.headline || '',
    subheadline: slide.subheadline || slide.subtitle || fallback.subheadline || '',
    body: slide.body || slide.description || fallback.body || '',
    cta: slide.cta || offerBox?.cta || fallback.cta || '',

    proof: slide.proof || fallback.proof || '',
    result: slide.result || testimonial?.result || fallback.result || '',
    question: slide.question || fallback.question || '',

    quote: slide.quote || testimonial?.quote || fallback.quote || '',
    author: slide.author || testimonial?.author || fallback.author || '',

    offer: slide.offer || offerBox?.offer || fallback.offer || '',
    urgency: slide.urgency || offerBox?.urgency || fallback.urgency || '',

    stat: slide.stat || stats?.[0]?.value || fallback.stat || '',
    statLabel: slide.statLabel || stats?.[0]?.label || fallback.statLabel || '',

    bullets,
    steps,
    timeline,

    beforeAfter: slide.beforeAfter || fallback.beforeAfter || null,
    stats,
    offerBox,
    testimonial,
  }
}

export function BeautyFrame({
  activeIndex = 0,
  total,
  onActiveIndexChange,
  children,
  className = '',
  nav = 'dark',
  borderRadius = 34,
  showControls = true,
}: {
  activeIndex?: number
  total: number
  onActiveIndexChange?: (index: number) => void
  children: ReactNode
  className?: string
  nav?: 'dark' | 'light'
  borderRadius?: number
  showControls?: boolean
}) {
  const safeTotal = Math.max(total, 1)
  const prev = () => onActiveIndexChange?.((activeIndex - 1 + safeTotal) % safeTotal)
  const next = () => onActiveIndexChange?.((activeIndex + 1) % safeTotal)
  const isLight = nav === 'light'

  const VIRTUAL_WIDTH = 533
  const VIRTUAL_HEIGHT = 666

  return (
    <div className="space-y-3">
      <div
        className={`relative w-full overflow-hidden ${className}`}
        style={{
          aspectRatio: `${VIRTUAL_WIDTH} / ${VIRTUAL_HEIGHT}`,
          borderRadius,
          boxShadow: '0 28px 90px rgba(0,0,0,0.16)',
          containerType: 'inline-size',
        }}
      >
        <div
          className="absolute left-0 top-0 overflow-hidden"
          style={{
            width: VIRTUAL_WIDTH,
            height: VIRTUAL_HEIGHT,
            transformOrigin: 'top left',
            transform: `scale(calc(100cqw / ${VIRTUAL_WIDTH}px))`,
          }}
        >
        {children}

        {showControls && safeTotal > 1 ? (
          <div className="absolute right-5 top-5 z-50 flex gap-2">
            <button
              type="button"
              onClick={prev}
              className="flex h-9 w-9 items-center justify-center rounded-full border backdrop-blur transition hover:scale-105"
              style={{
                background: isLight ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.26)',
                borderColor: isLight ? 'rgba(255,255,255,0.34)' : 'rgba(33,24,21,0.14)',
                color: isLight ? '#fff' : beautyInk,
              }}
              aria-label="Slide anterior"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={next}
              className="flex h-9 w-9 items-center justify-center rounded-full border backdrop-blur transition hover:scale-105"
              style={{
                background: isLight ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.26)',
                borderColor: isLight ? 'rgba(255,255,255,0.34)' : 'rgba(33,24,21,0.14)',
                color: isLight ? '#fff' : beautyInk,
              }}
              aria-label="Siguiente slide"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        ) : null}

        {showControls && safeTotal > 1 ? (
          <div className="absolute bottom-5 left-6 z-50 flex items-center gap-1.5">
            {Array.from({ length: safeTotal }).map((_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => onActiveIndexChange?.(idx)}
                className="h-1.5 rounded-full transition-all"
                style={{
                  width: idx === activeIndex ? 24 : 7,
                  background: isLight ? '#fff' : beautyInk,
                  opacity: idx === activeIndex ? 0.95 : 0.28,
                }}
                aria-label={`Ir al slide ${idx + 1}`}
              />
            ))}
          </div>
        ) : null}

        {showControls && safeTotal > 1 ? (
        <div
          className="absolute bottom-5 right-6 z-50 text-[10px] font-bold"
          style={{ color: isLight ? 'rgba(255,255,255,0.66)' : 'rgba(33,24,21,0.45)', fontFamily: beautySans }}
        >
          {activeIndex + 1}/{safeTotal}
        </div>
        ) : null}
      </div>
      </div>
    </div>
  )
}

export function FullBleedImage({
  src,
  blur,
  overlay = 'black',
  opacity = 0.35,
  objectPosition = 'center',
}: {
  src?: string | null
  blur?: boolean
  overlay?: BeautyOverlayTone
  opacity?: number
  objectPosition?: string
}) {
  return (
    <>
      {src ? (
        <img
          src={src}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          style={{
            objectPosition,
            filter: blur ? 'blur(14px)' : 'none',
            transform: blur ? 'scale(1.08)' : 'scale(1)',
          }}
        />
      ) : null}

      {overlay !== 'none' ? (
        <div
          className="absolute inset-0"
          style={{
            background: overlay === 'black' ? '#000000' : '#ffffff',
            opacity,
          }}
        />
      ) : null}
    </>
  )
}

export function SafeText({
  children,
  className = '',
  style,
}: {
  children: ReactNode
  className?: string
  style?: CSSProperties
}) {
  return (
    <div className={className} style={{ overflowWrap: 'anywhere', ...style }}>
      {children}
    </div>
  )
}

export function BeautyKicker({
  children,
  color = beautyMuted,
  borderColor = 'rgba(33,24,21,0.15)',
  background = 'transparent',
}: {
  children?: ReactNode
  color?: string
  borderColor?: string
  background?: string
}) {
  if (!children) return null

  return (
    <div
      className="inline-flex rounded-full border px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em]"
      style={{ color, borderColor, background, fontFamily: beautySans }}
    >
      {children}
    </div>
  )
}

export function BeautyNumber({
  value,
  color,
}: {
  value: string
  color: string
}) {
  return (
    <div
      className="text-[11px] font-bold uppercase tracking-[0.18em]"
      style={{ color, fontFamily: beautySans }}
    >
      {value}
    </div>
  )
}
